/**
 * GravityAnchor — captures the device-frame gravity vector via the
 * DeviceOrientation API. Used at recording-start time so downstream
 * Core modules (SubjectScaleEstimator, JARMMapper) can rotate the
 * camera-space coords to a true gravity-aligned world frame.
 *
 * Without this anchor the world Y axis follows the camera's pitch:
 * tilting the laptop or holding the phone forward biases every ROM
 * angle by the same offset. With it, the floor plane fit becomes
 * "gravity is here, regardless of where the camera points."
 *
 * iOS 13+ requires a one-shot user-gesture permission via
 * `DeviceOrientationEvent.requestPermission()`. This module exposes
 * `requestPermission()` so the host (a button click handler) can wire
 * it explicitly; auto-attempting without a gesture throws on iOS.
 *
 * Accuracy-UP step #4 of 6 from the brownfield techniques review.
 */

export interface GravityReading {
  /** Front-back tilt in degrees. Positive = device tilted forward
   *  (top edge away from user). 0 = vertical screen, 90 = lying flat. */
  beta: number | null;
  /** Left-right tilt in degrees. Positive = device tilted right. */
  gamma: number | null;
  /** Compass heading (rotation around screen normal) in degrees. */
  alpha: number | null;
  /** Wallclock-ms when this reading was captured. */
  timestampMs: number;
}

export type PermissionState = 'granted' | 'denied' | 'unsupported' | 'unknown';

interface DeviceOrientationEventConstructorWithPermission {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

let lastReading: GravityReading | null = null;
let listening = false;
let permissionState: PermissionState = 'unknown';

function onOrientation(ev: DeviceOrientationEvent): void {
  lastReading = {
    beta: ev.beta,
    gamma: ev.gamma,
    alpha: ev.alpha,
    timestampMs: Date.now(),
  };
}

function attachListener(): void {
  if (listening) return;
  if (typeof window === 'undefined') return;
  window.addEventListener('deviceorientation', onOrientation, { passive: true });
  listening = true;
}

/**
 * Request permission on iOS 13+ and start listening. Safe to call
 * from a user-gesture handler (touchend / click). On other browsers
 * this just attaches the listener and returns 'granted' if the
 * `deviceorientation` event is available, 'unsupported' otherwise.
 */
export async function requestGravityPermission(): Promise<PermissionState> {
  if (typeof window === 'undefined') {
    permissionState = 'unsupported';
    return permissionState;
  }
  const ctor = (
    window as unknown as { DeviceOrientationEvent?: DeviceOrientationEventConstructorWithPermission }
  ).DeviceOrientationEvent;
  if (!ctor) {
    permissionState = 'unsupported';
    return permissionState;
  }
  if (typeof ctor.requestPermission === 'function') {
    try {
      const state = await ctor.requestPermission();
      permissionState = state === 'granted' ? 'granted' : 'denied';
      if (state === 'granted') attachListener();
      return permissionState;
    } catch (err) {
      console.warn('[GravityAnchor] permission request failed', err);
      permissionState = 'denied';
      return permissionState;
    }
  }
  // Non-iOS path: attach immediately, no permission gate.
  attachListener();
  permissionState = 'granted';
  return permissionState;
}

/** Returns the most recent reading, or null when none has arrived
 *  yet. Listeners are attached lazily by requestGravityPermission(). */
export function getGravityReading(): GravityReading | null {
  return lastReading;
}

export function getGravityPermissionState(): PermissionState {
  return permissionState;
}

/**
 * Convert the latest reading into a 3×3 rotation matrix that takes
 * camera-frame vectors into a gravity-aligned world frame.
 * Returns the identity matrix when no reading is available.
 *
 * Convention:
 *   - World +Y is up (gravity opposite).
 *   - Camera +Z points away from the screen (out the back).
 *   - We use the beta angle to pitch the camera frame back to
 *     vertical; gamma to roll. Heading (alpha) is left untouched
 *     because it's relative to magnetic north, which we don't need
 *     for ROM measurement.
 */
export function buildGravityRotation(): [
  number, number, number,
  number, number, number,
  number, number, number,
] {
  const r = lastReading;
  if (!r || r.beta === null || r.gamma === null) {
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }
  const betaRad = (r.beta * Math.PI) / 180;
  const gammaRad = (r.gamma * Math.PI) / 180;
  const cb = Math.cos(betaRad);
  const sb = Math.sin(betaRad);
  const cg = Math.cos(gammaRad);
  const sg = Math.sin(gammaRad);
  // Roll (gamma) about Z, then pitch (beta) about X. Composition R = Rx * Rz.
  const m: [number, number, number, number, number, number, number, number, number] = [
    cg, -sg, 0,
    cb * sg, cb * cg, -sb,
    sb * sg, sb * cg, cb,
  ];
  return m;
}
